// Replaces the legacy MakeStartTimeline / MakeGatherTimeline / MakeWaitTimeline
// trio (three near-duplicate functions) with one makeTimelineEntry(phase, ...).
// A few legacy quirks are preserved exactly (not "fixed") for regression
// parity, since they're display-only and not documented as intentional
// divergences in plan.md:
//   - BUILD_SHIP's "wait" phase label omits the ship count (start/gather
//     include it).
//   - TRANSPORT's "wait" phase reports the *target* planet id but computes
//     startTime/endTime from the *source* planet's clock.
//   - SET_PROD's summary is the fixed string "Set Production" (no
//     Start/Gather/Await prefix) since it only ever appears in 'start' phase.
import { actionTypes, buildingData, researchData, shipData, defenseData } from '../data';

const PREFIX = { start: 'Start ', gather: 'Gather ', wait: 'Await ' };
const SUFFIX = { start: '', gather: 'G', wait: 'W' };

const buildingLabel = (state, planetId, building) =>
  `${buildingData[building].name} ${state.planets[planetId].buildings[building] + 1}`;

const researchLabel = (state, tech) => `${researchData[tech].name} ${state.research[tech] + 1}`;

const shipLabel = (ship, count) => `${shipData[ship].name}${count !== undefined ? ' ' + count : ''}`;

const defenseLabel = (defense, count) => `${defenseData[defense].name}${count !== undefined ? ' ' + count : ''}`;

const actionLabel = (phase, state, planetId, action) => {
  const { type, payload } = action;
  switch (type) {
    case actionTypes.UPGRADE_BUILDING:
      return buildingLabel(state, planetId, payload.building);
    case actionTypes.RESEARCH:
      return researchLabel(state, payload.tech);
    case actionTypes.BUILD_SHIP:
      return shipLabel(payload.ship, phase === 'wait' ? undefined : payload.count);
    case actionTypes.BUILD_DEFENSE:
      return defenseLabel(payload.defense, phase === 'wait' ? undefined : payload.count);
    default:
      return undefined;
  }
};

const makeSetProdEntry = (state, action, stateBefore, stateAfter) => {
  const { payload, index, duration } = action;
  const { planetId } = payload;
  return {
    valid: true,
    editable: true,
    index,
    planetId,
    startTime: state.planets[planetId].time,
    endTime: state.planets[planetId].time + duration,
    summary: 'Set Production',
    displayType: 'SET_PROD',
    payload,
    stateBefore,
    stateAfter,
  };
};

const makeTransportEntry = (phase, state, action, stateBefore, stateAfter) => {
  const { type, payload, index, duration } = action;
  const { planetId, settings } = payload; // planetId = source
  if (phase === 'start') {
    return {
      valid: true,
      editable: true,
      index,
      planetId,
      startTime: state.planets[planetId].time,
      endTime: state.planets[planetId].time, // instantaneous marker
      summary: 'Start Transport',
      displayType: 'TRANSPORT',
      payload,
      stateBefore,
      stateAfter,
    };
  }
  if (phase === 'gather') {
    return {
      valid: true,
      editable: false,
      index: index + 'G',
      planetId,
      startTime: state.planets[planetId].time,
      endTime: state.planets[planetId].time + duration,
      summary: 'Gather Transport',
      stateBefore,
      stateAfter,
    };
  }
  // phase === 'wait': reports the target planet but times off the source
  // planet's clock, matching the legacy quirk exactly.
  return {
    valid: true,
    editable: false,
    type,
    index: index + 'W',
    planetId: settings.target,
    startTime: state.planets[planetId].time,
    endTime: state.planets[planetId].time + duration,
    summary: 'Await Transport',
    stateBefore,
    stateAfter,
  };
};

const makeGenericEntry = (phase, state, action, stateBefore, stateAfter) => {
  const { type, payload, index, duration } = action;
  const { planetId } = payload;
  return {
    valid: true,
    editable: phase === 'start',
    ...(phase === 'wait' ? { type } : {}),
    // '+' would coerce a numeric index to a string even when the suffix is
    // '' (start phase) — only concatenate when there's an actual suffix.
    index: SUFFIX[phase] ? index + SUFFIX[phase] : index,
    planetId,
    startTime: state.planets[planetId].time,
    endTime: state.planets[planetId].time + duration,
    summary: PREFIX[phase] + actionLabel(phase, state, planetId, action),
    stateBefore,
    stateAfter,
  };
};

export const makeTimelineEntry = (phase, state, action, stateBefore, stateAfter) => {
  const { type } = action;
  if (type === actionTypes.SET_PROD) return makeSetProdEntry(state, action, stateBefore, stateAfter);
  if (type === actionTypes.TRANSPORT) return makeTransportEntry(phase, state, action, stateBefore, stateAfter);
  return makeGenericEntry(phase, state, action, stateBefore, stateAfter);
};

// Replaces ~7 duplicated `{valid:false, editable:true, ...}` blocks.
// planetId may be missing from state.planets (e.g. a stale reference to a
// deleted planet) — falls back to global state.time rather than crashing,
// which is what the legacy code did if this path was ever hit.
export const makeInvalidEntry = (state, planetId, index, summary) => {
  const time = state.planets[planetId] ? state.planets[planetId].time : state.time;
  return {
    valid: false,
    editable: true,
    index,
    planetId,
    startTime: time,
    endTime: time,
    summary,
  };
};
